/**
 * The capture screen: viewfinder, store, card total, save.
 *
 * The one screen with a hard performance target - four seconds one-handed at a
 * till - and the one where a wrong default is expensive, because a receipt saved
 * against the wrong shop looks exactly like a receipt saved against the right
 * one. Everything here follows from those two facts.
 *
 * Nothing in this file talks to the network. Saving is a write to IndexedDB and
 * nothing else; `queue.ts` uploads on its own schedule. That is what makes the
 * screen usable in the cold-aisle dead spot at the back of a shop.
 */
import { useEffect, useRef, useState } from "preact/hooks";

import { enqueue, mintCaptureId, type QueueItem } from "./queue.js";

/**
 * Long edge of the stored photo, in pixels.
 *
 * A modern phone sensor at full resolution encodes to several megabytes, which
 * is under the 12 MB limit and roughly ten times BUILD-SPEC 12's measured
 * 716 KB per photo - the figure the whole capacity and transfer model rests on.
 * 2000px is comfortably enough to read a thermal receipt and lands in the range
 * those figures assume.
 *
 * Applied to attached photos as well as live ones. A photo from the camera roll
 * is full-sensor by definition, so exempting it would put the largest files in
 * the system on the path that was meant to be the cheap one.
 */
const MAX_EDGE = 2000;
const QUALITY = 0.85;

/** A photo waiting to be saved, and when it was actually taken. */
interface Shot {
  blob: Blob;
  /** For a live capture, now. For an attached file, the file's own timestamp -
   *  a receipt photographed last Tuesday should not enter the ledger as today. */
  takenAt: number;
}

/**
 * Re-encode a chosen file to the same budget a live capture gets.
 *
 * `imageOrientation: "from-image"` is load-bearing and easy to miss: a canvas
 * drops EXIF, so if the orientation is not applied while reading it, it is gone
 * by the time the server sees the bytes - and a receipt the agent reads sideways
 * is a receipt it reads badly. The server rotates too, but only using EXIF that
 * this step would already have destroyed.
 *
 * Falls back to the original bytes on any failure. A slightly large photo is a
 * far better outcome than a capture that refuses to save.
 */
async function downscale(file: Blob): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const out = await new Promise<Blob | null>((r) =>
      canvas.toBlob((b) => r(b), "image/jpeg", QUALITY),
    );
    return out ?? file;
  } catch {
    return file;
  }
}

/**
 * The store recorded when you have not said which shop it was.
 *
 * The default deliberately, rather than the first configured chip. A chip that
 * arrives preselected is one you can save against without ever looking at it,
 * and a receipt filed under the wrong shop is worse than one filed under none:
 * the agent reads the store off the photo anyway, but it has no way to know it
 * should disagree with an answer you appear to have given.
 */
const NO_STORE = "unknown";

/**
 * Turn a getUserMedia failure into something you can act on.
 *
 * The raw messages are useless in the one place this matters. "The request is
 * not allowed by the user agent or the platform in the current context" is what
 * Chrome says for a permission the user denied, a permission Windows revoked
 * from the browser itself, and a home-screen PWA on iOS that was never granted
 * one - three problems with three different fixes and one sentence between
 * them. `err.name` is the part worth branching on; the message is not.
 *
 * `null` means the API was absent rather than rejected, which is what an
 * insecure origin looks like: no error, no prompt, nothing. Serving over a LAN
 * IP is the way to hit it, and it is invisible unless it is named.
 *
 * Exported so it can be tested without a camera, which is the only way it ever
 * gets tested - the failures worth handling are precisely the ones that cannot
 * be reproduced in CI.
 */
/**
 * Release a camera, defensively.
 *
 * This runs in an effect cleanup, and a throw there aborts Preact's unmount -
 * so a stream that is not quite the shape expected takes the whole screen down
 * with it, on the way out, where it is hardest to diagnose. The failure this
 * guards against is also the one with a visible consequence: tracks that are
 * never stopped leave the indicator light on.
 */
function stopTracks(stream: MediaStream | null): void {
  try {
    stream?.getTracks?.().forEach((t) => t.stop());
  } catch {
    /* nothing useful to do; do not take the unmount with us */
  }
}

export function cameraProblem(err: unknown): string {
  if (err === null) {
    if (typeof isSecureContext === "boolean" && !isSecureContext) {
      return (
        "The camera needs a secure connection, and this page is plain http on a " +
        "network address. Open it on http://localhost, or over an https tunnel."
      );
    }
    return "This browser does not offer camera access.";
  }

  const name = err instanceof Error ? err.name : "";
  const detail = err instanceof Error ? err.message : String(err);

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return (
        "Permission to use the camera was refused. Check the camera icon in the " +
        "address bar; on Windows also check Settings → Privacy & security → " +
        "Camera → “Let desktop apps access your camera”. If this is a home-screen " +
        "app on iOS, it asks separately from Safari and only asks once."
      );
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No camera was found on this device.";
    case "NotReadableError":
    case "TrackStartError":
      return "The camera is being used by another app. Close it and try again.";
    case "OverconstrainedError":
      return "No camera matched what the app asked for.";
    case "SecurityError":
      return "The browser blocked camera access on this page for security reasons.";
    case "AbortError":
      return "Starting the camera was interrupted. Try again.";
    default:
      return name ? `${name}: ${detail}` : detail;
  }
}

// ------------------------------------------------------------------------

/** One saved shot this session, for the feedback strip. */
interface SessionShot {
  id: string;
  url: string | null;
  at: number;
}

export function Capture({
  stores,
  onCaptured,
  queueItems = [],
}: {
  stores: string[];
  onCaptured: () => void;
  /** The live local queue, so each shot in the strip can say whether it is
   *  still on this phone or safely on the server. */
  queueItems?: QueueItem[];
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /**
   * A ref, not state.
   *
   * The cleanup below runs with the closure it was created in, and with an
   * empty dep array that closure captured `stream` as null - so stopping the
   * tracks did nothing and the camera light stayed on after leaving the tab.
   * A ref is read at cleanup time rather than captured at mount time.
   */
  const streamRef = useRef<MediaStream | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [store, setStore] = useState(NO_STORE);
  const [cardTotal, setCardTotal] = useState("");
  const [shot, setShot] = useState<Shot | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saved, setSaved] = useState(0);
  const [busy, setBusy] = useState(false);
  /**
   * `resting` is what "Save & done" produces: camera released, form gone, queue
   * visible underneath. The camera is not merely hidden - a live stream behind a
   * panel keeps the indicator light on and the battery draining, which on a
   * phone in a pocket is the difference between a feature and a complaint.
   */
  const [resting, setResting] = useState(false);
  /** Bumped by "Try again"; the only purpose is to re-run the effect below. */
  const [retry, setRetry] = useState(0);
  /** True for a moment after each save - the shutter-flash feedback. */
  const [flash, setFlash] = useState(false);
  /** Everything saved this session, newest first, for the strip. */
  const [shots, setShots] = useState<SessionShot[]>([]);
  const shotsRef = useRef<SessionShot[]>([]);
  shotsRef.current = shots;

  // Object URLs in the strip are released together on unmount; per-shot
  // revocation would break the strip's own <img> while it is visible.
  useEffect(
    () => () => {
      for (const s of shotsRef.current) if (s.url) URL.revokeObjectURL(s.url);
    },
    [],
  );

  function recordShot(id: string, blob: Blob) {
    const url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : null;
    setShots((prev) => [{ id, url, at: Date.now() }, ...prev]);
    setFlash(true);
    setTimeout(() => setFlash(false), 220);
  }

  useEffect(() => {
    if (resting) return;
    let active = true;

    // No mediaDevices at all is almost always an insecure context, and it is
    // the one failure the browser does not report: the API is simply absent, so
    // there is no rejection to catch. Left alone it shows a black rectangle and
    // no explanation - and, worse, no way to reach the attach-a-photo fallback,
    // which only appears once something has gone wrong.
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError(cameraProblem(null));
      return;
    }

    // Rear camera. `ideal` rather than `exact` so a laptop webcam still works -
    // otherwise the whole screen is dead on any device without one.
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
      .then((s) => {
        if (!active) {
          stopTracks(s);
          return;
        }
        setCamError(null);
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch((e) => setCamError(cameraProblem(e)));
    return () => {
      active = false;
      stopTracks(streamRef.current);
      streamRef.current = null;
    };
  }, [resting, retry]);

  // Object URLs are not garbage collected on their own; an unreleased one per
  // attached photo is a megabyte each until the tab closes.
  useEffect(() => {
    if (!shot || typeof URL.createObjectURL !== "function") {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(shot.blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [shot]);

  async function takePhoto(): Promise<Shot | null> {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;

    const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d")!.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", QUALITY),
    );
    return blob ? { blob, takenAt: Date.now() } : null;
  }

  async function attach(file: File) {
    setBusy(true);
    try {
      setShot({ blob: await downscale(file), takenAt: file.lastModified || Date.now() });
    } finally {
      setBusy(false);
    }
  }

  /**
   * A multi-selection skips the preview and queues every photo directly, one
   * capture each. The preview-then-save flow exists so you can check the shot
   * you just took; a backlog picked from the camera roll was checked when it
   * was taken, and previewing eleven photos one at a time is the annoyance
   * this path removes.
   *
   * `card_total` is deliberately null for all of them - it belongs to exactly
   * one receipt, and applying one figure to N receipts would hold N-1 of them
   * at the R21 check. The store chip does apply: bulk-attaching a stack from
   * one shop with the chip set is the intended use.
   */
  async function attachMany(files: File[]) {
    setBusy(true);
    try {
      for (const file of files) {
        const blob = await downscale(file);
        const id = mintCaptureId();
        await enqueue(
          {
            capture_id: id,
            captured_at: new Date(file.lastModified || Date.now()).toISOString(),
            store,
            card_total: null,
            currency: "EUR",
            page_of: null,
            note: null,
          },
          blob,
        );
        setSaved((n) => n + 1);
        recordShot(id, blob);
      }
      onCaptured();
    } finally {
      setBusy(false);
    }
  }

  async function save(then: "again" | "done") {
    if (busy) return; // a double-tap at a till must not queue the same shot twice
    setBusy(true);
    try {
      const src = shot ?? (await takePhoto());
      if (!src) return;

      // The id is minted here, before anything touches the network. That
      // ordering is the whole no-duplicate guarantee: a retry after an ambiguous
      // timeout carries the same id and the server answers duplicate:true.
      const id = mintCaptureId();
      await enqueue(
        {
          capture_id: id,
          captured_at: new Date(src.takenAt).toISOString(),
          store,
          card_total: cardTotal ? Number(cardTotal) : null,
          currency: "EUR",
          page_of: null,
          note: null,
        },
        src.blob,
      );

      setSaved((n) => n + 1);
      recordShot(id, src.blob);
      setShot(null);
      setCardTotal("");
      onCaptured();
      // Store is retained across "add another": the next receipt is usually the
      // next shop in the same trip, or the same shop again. "Done" ends the
      // trip, so it goes back to unset rather than carrying a stale answer into
      // whenever you next open the app.
      if (then === "done") {
        setStore(NO_STORE);
        setResting(true);
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * The Done pill. With a shot pending it saves that shot and rests; with
   * nothing pending it just ends the session. It never takes a photo of its
   * own - a surprise capture on the way out is worse than none.
   */
  async function finish() {
    if (busy) return;
    if (shot) {
      await save("done");
      return;
    }
    setStore(NO_STORE);
    setResting(true);
  }

  if (resting) {
    return (
      <div class="capture">
        <div class="card done">
          <h2>Saved</h2>
          <p class="muted">
            {saved} {saved === 1 ? "receipt" : "receipts"} this session. Anything still
            listed below is uploading - you can close the app, it finishes next time you
            open it.
          </p>
          <button class="primary" type="button" onClick={() => setResting(false)}>
            Capture again
          </button>
        </div>
      </div>
    );
  }

  const picker = (
    <input
      type="file"
      accept="image/*"
      multiple
      onChange={(e) => {
        const files = Array.from((e.target as HTMLInputElement).files ?? []);
        // Cleared so choosing the same file twice fires change again.
        (e.target as HTMLInputElement).value = "";
        if (files.length === 1) void attach(files[0]!);
        else if (files.length > 1) void attachMany(files);
      }}
    />
  );

  return (
    <div class="capture">
      <div class="viewfinder">
        {camError ? (
          <div class="camera-fallback">
            <p class="error">Camera unavailable</p>
            <p class="small">{camError}</p>
            <div class="fallback-actions">
              <label class="filepick">Choose a photo{picker}</label>
              {/* Granting permission does not restart a stream that already
                  failed, and without this the only way back is a reload - which
                  on a home-screen app means finding the reload. */}
              <button type="button" onClick={() => setRetry((n) => n + 1)}>
                Try again
              </button>
            </div>
          </div>
        ) : (
          <video ref={videoRef} autoplay playsinline muted />
        )}

        {shot && (
          <div class="shot-preview">
            {preview ? <img src={preview} alt="Attached receipt" /> : <p>Photo attached</p>}
            <button
              class="discard"
              type="button"
              aria-label="Discard this photo"
              onClick={() => setShot(null)}
            >
              ×
            </button>
          </div>
        )}

        {!camError && !shot && (
          <label class="attach" title="Attach photos you already have">
            {/* Stacked-rectangles glyph: a picture behind a picture. */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="7" y="3" width="14" height="11" rx="2" />
              <path d="M17 17H5a2 2 0 0 1-2-2V7" />
              <path d="m9 11 3-3 4 4" />
            </svg>
            <span class="sr-only">Attach photos you already have</span>
            {picker}
          </label>
        )}

        {/* Everything needed to shoot lives ON the viewfinder, so the camera
            never scrolls out of view to reach a button. */}
        {(!camError || shot) && (
          <div class="cam-controls">
            <button
              type="button"
              class="shutter"
              aria-label="Take photo"
              disabled={busy}
              onClick={() => void save("again")}
            />
            <button type="button" class="donebtn" disabled={busy} onClick={() => void finish()}>
              Done
            </button>
          </div>
        )}

        {flash && <div class="flash" />}
      </div>

      <div class="chips">
        {stores.map((s) => (
          <button
            key={s}
            class={`chip ${s === store ? "on" : ""}`}
            onClick={() => setStore(s)}
            type="button"
          >
            {s}
          </button>
        ))}
        <button
          class={`chip ${!stores.includes(store) ? "on" : ""}`}
          type="button"
          onClick={() => {
            const other = prompt("Store name", store === NO_STORE ? "" : store);
            if (other !== null) setStore(other.trim() || NO_STORE);
          }}
        >
          {/* Once you have typed a name it becomes the label, because "Other…"
              highlighted tells you a choice was made but not which one. */}
          {store === NO_STORE || stores.includes(store) ? "Other…" : store}
        </button>
      </div>

      <label class="total">
        Card total
        <input
          type="text"
          inputmode="decimal"
          placeholder="skip if unsure"
          value={cardTotal}
          onInput={(e) => setCardTotal((e.target as HTMLInputElement).value.replace(/[^\d.]/g, ""))}
        />
      </label>
      <p class="muted small">
        What the terminal charged, deposits included. Leave it blank rather than guess -
        a wrong figure holds the receipt, a missing one just skips a check.
      </p>

      {shots.length > 0 && (
        <div class="shots" aria-label="Saved this session">
          {shots.map((s) => {
            const q = queueItems.find((i) => i.capture_id === s.id);
            const state = q ? q.state : "done";
            const label =
              state === "done"
                ? "On the server - the paper can go"
                : state === "failed"
                  ? `Failed: ${q?.error_code ?? "needs a look"}`
                  : "Still on this phone - keep the paper";
            return (
              <span class={`shotchip ${state}`} key={s.id} title={label}>
                {s.url ? <img src={s.url} alt="Saved receipt" /> : <span class="ph" />}
                <span class="statusdot" aria-label={label} />
              </span>
            );
          })}
          <p class="muted small legend">
            {saved} saved · green means it is on the server and the paper receipt can go
          </p>
        </div>
      )}
    </div>
  );
}
